const AUDIO_FILE_EXTENSIONS = [".wav", ".mp3", ".m4a"] as const
const TRANSCRIPT_FILE_EXTENSIONS = [".txt", ".json"] as const
const TRACES_FILE_EXTENSION = ".json"
const FALLBACK_AUDIO_EXTENSION = "wav"
const MAX_TRACES_FILE_SIZE_BYTES = 10 * 1024 * 1024

interface TranscriptMessage {
  role: "user" | "assistant" | "system"
  content?: string
  message?: string
  timestamp?: string
}

export interface TracesData {
  resourceSpans: unknown[]
  // OTel payloads carry additional fields; the index signature keeps the type
  // assignable to the transport's Record<string, unknown> traces parameter.
  [key: string]: unknown
}

type TranscriptFileValidation = { ok: true } | { ok: false; error: string }
type AudioFileValidation = { ok: true } | { ok: false; error: string }

type TracesFileParseResult =
  | { ok: true; data: TracesData }
  | { ok: false; error: string }

type AudioUrlValidationResult = { ok: true } | { ok: false; error: string }

/**
 * Returns the lowercased dotted extension of a filename (e.g. ".json").
 * Falls back to "." when the name has no extension.
 */
export function getFileExtension(fileName: string): string {
  return `.${fileName.split(".").pop()?.toLowerCase() ?? ""}`
}

/**
 * Returns the lowercased bare extension of a filename, or the fallback when
 * none is present (used to choose an extension for the signed audio upload).
 */
export function getAudioFileExtension(
  fileName: string,
  fallback: string = FALLBACK_AUDIO_EXTENSION,
): string {
  return fileName.split(".").pop()?.toLowerCase() || fallback
}

/** Validates that an audio upload has an explicitly supported extension. */
export function validateAudioFile(file: File): AudioFileValidation {
  const fileExtension = getFileExtension(file.name)

  if (
    !AUDIO_FILE_EXTENSIONS.includes(
      fileExtension as (typeof AUDIO_FILE_EXTENSIONS)[number],
    )
  ) {
    return {
      ok: false,
      error: "Invalid file type. Please upload a .wav, .mp3, or .m4a file.",
    }
  }

  return { ok: true }
}

/**
 * Parses transcript text into messages. JSON arrays map each entry to a
 * message; anything else is treated as plain text, one message per non-empty
 * line, inferring the role from a leading "user:"/"assistant:"/"agent:" prefix.
 */
export function parseTranscript(text: string): TranscriptMessage[] {
  try {
    const parsed: unknown = JSON.parse(text)
    if (Array.isArray(parsed)) {
      if (
        !parsed.every(
          (message) =>
            typeof message === "object" &&
            message !== null &&
            !Array.isArray(message),
        )
      ) {
        return []
      }

      return parsed.map((message) => {
        const msg = message as Record<string, unknown>
        const role = (msg.role as string) || "user"
        return {
          role: role as "user" | "assistant" | "system",
          content: (msg.content as string) || (msg.message as string) || "",
          timestamp: msg.timestamp as string | undefined,
        }
      })
    }
  } catch {
    // Plain text parsing
    const lines = text.trim().split("\n")
    const messages: TranscriptMessage[] = []

    lines.forEach((line) => {
      const trimmed = line.trim()
      if (!trimmed) {
        return
      }

      let role: "user" | "assistant" = "user"
      let content = trimmed

      if (trimmed.toLowerCase().startsWith("user:")) {
        role = "user"
        content = trimmed.substring(5).trim()
      } else if (
        trimmed.toLowerCase().startsWith("assistant:") ||
        trimmed.toLowerCase().startsWith("agent:")
      ) {
        role = "assistant"
        content = trimmed.substring(trimmed.indexOf(":") + 1).trim()
      }

      messages.push({ role, content })
    })

    return messages
  }

  return []
}

/**
 * Validates that a transcript upload has an allowed (.txt/.json) extension.
 */
export function validateTranscriptFile(file: File): TranscriptFileValidation {
  const fileExtension = getFileExtension(file.name)

  if (
    !TRANSCRIPT_FILE_EXTENSIONS.includes(
      fileExtension as (typeof TRANSCRIPT_FILE_EXTENSIONS)[number],
    )
  ) {
    return {
      ok: false,
      error: "Invalid file type. Please upload a .txt or .json file.",
    }
  }

  return { ok: true }
}

/** Validates that an audio source is a parseable HTTP(S) URL. */
export function validateAudioUrl(rawUrl: string): AudioUrlValidationResult {
  let url: URL
  try {
    url = new URL(rawUrl)
  } catch {
    return { ok: false, error: "Enter a valid audio URL." }
  }

  if (url.protocol !== "http:" && url.protocol !== "https:") {
    return { ok: false, error: "Audio URLs must use HTTP or HTTPS." }
  }

  return { ok: true }
}

/**
 * Reads, validates and parses a traces upload, enforcing the size limit, the
 * .json extension, and the presence of an OTel `resourceSpans` array.
 */
export async function parseTracesFile(
  file: File,
): Promise<TracesFileParseResult> {
  if (file.size > MAX_TRACES_FILE_SIZE_BYTES) {
    return { ok: false, error: "Traces file is too large (max 10 MB)." }
  }

  const fileExtension = getFileExtension(file.name)
  if (fileExtension !== TRACES_FILE_EXTENSION) {
    return {
      ok: false,
      error: "Invalid file type. Please upload a .json file.",
    }
  }

  try {
    const text = await file.text()
    const parsed = JSON.parse(text) as {
      resourceSpans?: unknown[]
    } | null

    if (!parsed?.resourceSpans || !Array.isArray(parsed.resourceSpans)) {
      return {
        ok: false,
        error: "Invalid OTel traces format: missing resourceSpans array.",
      }
    }

    return { ok: true, data: parsed as TracesData }
  } catch {
    return { ok: false, error: "Failed to parse JSON file." }
  }
}

/**
 * Converts a browser-local datetime value to an ISO timestamp. A blank or
 * invalid value means the conversation should use its upload time instead.
 */
export function buildOccurredAtISO(localDateTime: string): string | null {
  if (!localDateTime) {
    return null
  }

  try {
    return new Date(localDateTime).toISOString()
  } catch {
    return null
  }
}
